// src/pages/Notifications.jsx
import React, { useState, useEffect } from 'react';
import { collection, addDoc, updateDoc, deleteDoc, doc, query, orderBy, onSnapshot } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase/config';
import LoadingSpinner from '../components/LoadingSpinner';

const Notifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingNotif, setEditingNotif] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    important: false,
    fileUrl: ''
  });
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState('');
  const [currentTime, setCurrentTime] = useState('');
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const updateClock = () => {
      const now = new Date();
      setCurrentTime(now.toLocaleDateString('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      }));
    };
    updateClock();
    const clockInterval = setInterval(updateClock, 1000);

    const q = query(collection(db, 'notifications'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const notifData = [];
      snapshot.forEach((doc) => {
        notifData.push({ id: doc.id, ...doc.data() });
      });
      setNotifications(notifData);
      setLoading(false);
    });

    return () => {
      clearInterval(clockInterval);
      unsubscribe();
    };
  }, []);

  const resetForm = () => {
    setFormData({ title: '', important: false, fileUrl: '' });
    setFile(null);
    setFilePreview('');
    setEditingNotif(null);
    setUploadProgress(0);
    setIsUploading(false);
  };

  const openCreateModal = () => {
    resetForm();
    setModalOpen(true);
  };

  const openEditModal = (notif) => {
    setEditingNotif(notif);
    setFormData({
      title: notif.title || '',
      important: notif.important || false,
      fileUrl: notif.fileUrl || ''
    });
    if (notif.fileUrl) setFilePreview(notif.fileUrl);
    setModalOpen(true);
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setFilePreview(selectedFile.name);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setIsUploading(true);

    try {
      let fileUrl = formData.fileUrl;

      if (file) {
        const storageRef = ref(storage, `notifications/${Date.now()}_${file.name}`);
        const uploadTask = uploadBytesResumable(storageRef, file);
        
        await new Promise((resolve, reject) => {
          uploadTask.on(
            'state_changed',
            (snapshot) => {
              const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              setUploadProgress(progress);
            },
            (error) => reject(error),
            async () => {
              const url = await getDownloadURL(uploadTask.snapshot.ref);
              fileUrl = url;
              resolve();
            }
          );
        });
      }

      const notifData = {
        title: formData.title,
        important: formData.important,
        fileUrl: fileUrl || '',
        updatedAt: new Date()
      };

      if (editingNotif) {
        if (formData.fileUrl && file && formData.fileUrl !== fileUrl) {
          try { await deleteObject(ref(storage, formData.fileUrl)); } catch (e) {}
        }
        await updateDoc(doc(db, 'notifications', editingNotif.id), notifData);
        alert('Notification updated successfully!');
      } else {
        notifData.createdAt = new Date();
        await addDoc(collection(db, 'notifications'), notifData);
        alert('Notification created successfully!');
      }

      setModalOpen(false);
      resetForm();
    } catch (error) {
      console.error('Error saving notification:', error);
      alert('Error saving notification: ' + error.message);
    } finally {
      setLoading(false);
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDelete = async (id, fileUrl) => {
    if (!window.confirm('Are you sure you want to delete this notification?')) return;
    
    setLoading(true);
    try {
      if (fileUrl) {
        try {
          await deleteObject(ref(storage, fileUrl));
        } catch (e) {
          console.warn('File not found in storage');
        }
      }
      await deleteDoc(doc(db, 'notifications', id));
      alert('Notification deleted successfully!');
    } catch (error) {
      console.error('Error deleting notification:', error);
      alert('Error deleting notification: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    resetForm();
  };

  const getFilteredNotifications = () => {
    if (filter === 'important') {
      return notifications.filter(n => n.important === true);
    }
    if (filter === 'normal') {
      return notifications.filter(n => n.important !== true);
    }
    return notifications;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'No date';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch {
      return 'Invalid date';
    }
  };

  const getTimeAgo = (timestamp) => {
    if (!timestamp) return 'Recently';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      const seconds = Math.floor((new Date() - date) / 1000);
      if (seconds < 60) return 'just now';
      const minutes = Math.floor(seconds / 60);
      if (minutes < 60) return `${minutes} min ago`;
      const hours = Math.floor(minutes / 60);
      if (hours < 24) return `${hours} hr ago`;
      const days = Math.floor(hours / 24);
      if (days < 7) return `${days} days ago`;
      return formatDate(timestamp);
    } catch {
      return 'Recently';
    }
  };

  const styles = `
    .notifications-page {
      animation: structuralSlideUp 0.5s cubic-bezier(0.16, 1, 0.3, 1) both;
    }

    .management-toolbar {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 32px;
      background: rgba(255, 255, 255, 0.3);
      border: 1px solid rgba(255, 255, 255, 0.6);
      padding: 16px 24px;
      border-radius: 16px;
      backdrop-filter: blur(20px) saturate(140%);
      -webkit-backdrop-filter: blur(20px) saturate(140%);
      flex-wrap: wrap;
      gap: 16px;
    }

    .filter-select {
      padding: 10px 20px;
      border: 2px solid rgba(14, 165, 233, 0.2);
      border-radius: 12px;
      background: rgba(255, 255, 255, 0.6);
      cursor: pointer;
      font-weight: 700;
      font-size: 0.85rem;
      color: #334155;
      outline: none;
      transition: all 0.2s;
    }

    .filter-select:focus {
      border-color: #0ea5e9;
      background: #ffffff;
      box-shadow: 0 0 0 4px rgba(14, 165, 233, 0.1);
    }

    .btn-action-trigger {
      padding: 12px 24px;
      border: none;
      border-radius: 12px;
      cursor: pointer;
      font-weight: 700;
      font-size: 0.85rem;
      transition: all 0.3s ease;
      display: inline-flex;
      align-items: center;
      gap: 8px;
      box-shadow: 0 4px 15px rgba(14, 165, 233, 0.2);
    }

    .btn-action-trigger:hover {
      transform: translateY(-3px);
    }

    .btn-blue {
      background: linear-gradient(135deg, #0ea5e9, #0284c7);
      color: white;
    }

    .btn-blue:hover {
      box-shadow: 0 10px 25px rgba(14, 165, 233, 0.4);
    }

    .notifications-container {
      background: rgba(255, 255, 255, 0.45);
      backdrop-filter: blur(20px) saturate(140%);
      -webkit-backdrop-filter: blur(20px) saturate(140%);
      border-radius: 16px;
      overflow: hidden;
      border: 1px solid rgba(255, 255, 255, 0.6);
      box-shadow: 0 8px 32px 0 rgba(14, 165, 233, 0.06);
    }

    .notification-item {
      display: flex;
      align-items: center;
      padding: 24px;
      border-bottom: 1px solid rgba(14, 165, 233, 0.1);
      transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
    }

    .notification-item:hover {
      background: rgba(255, 255, 255, 0.6);
      transform: translateX(6px);
    }

    .notification-item:last-child {
      border-bottom: none;
    }

    .notification-icon-box {
      width: 48px;
      height: 48px;
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-right: 20px;
      flex-shrink: 0;
      font-size: 16px;
      border: 1px solid transparent;
    }

    .icon-important {
      background: rgba(245, 158, 11, 0.12);
      color: #f59e0b;
      border-color: rgba(245, 158, 11, 0.2);
    }

    .icon-normal {
      background: rgba(14, 165, 233, 0.12);
      color: #0ea5e9;
      border-color: rgba(14, 165, 233, 0.2);
    }

    .notification-content-body {
      flex: 1;
      min-width: 0;
    }

    .notification-item-title {
      font-size: 1.05rem;
      font-weight: 700;
      color: #0f172a;
      margin-bottom: 6px;
      display: flex;
      align-items: center;
      gap: 10px;
      flex-wrap: wrap;
    }

    .notification-item-meta {
      display: flex;
      gap: 16px;
      font-size: 0.78rem;
      color: #64748b;
      font-weight: 600;
    }

    .important-badge {
      display: inline-block;
      padding: 2px 10px;
      background: rgba(245, 158, 11, 0.12);
      color: #f59e0b;
      border: 1px solid rgba(245, 158, 11, 0.25);
      border-radius: 4px;
      font-size: 0.7rem;
      font-weight: 800;
      text-transform: uppercase;
    }

    .file-attachment-pill {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 6px 14px;
      background: rgba(255, 255, 255, 0.5);
      border: 1px solid rgba(14, 165, 233, 0.2);
      border-radius: 8px;
      font-size: 0.8rem;
      font-weight: 700;
      color: #0ea5e9;
      margin-top: 10px;
      text-decoration: none;
      transition: all 0.2s;
    }

    .file-attachment-pill:hover {
      background: #0ea5e9;
      color: #ffffff;
      border-color: #0ea5e9;
    }

    .notification-actions-cell {
      display: flex;
      gap: 10px;
      flex-shrink: 0;
    }

    .btn-row-action {
      padding: 8px 14px;
      border-radius: 8px;
      font-weight: 700;
      font-size: 0.8rem;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
      transition: all 0.2s;
      border: none;
    }

    .btn-row-edit {
      background: rgba(14, 165, 233, 0.12);
      border: 1px solid rgba(14, 165, 233, 0.3);
      color: #0ea5e9;
    }

    .btn-row-edit:hover {
      background: #0ea5e9;
      color: white;
    }

    .btn-row-delete {
      background: rgba(244, 63, 94, 0.1);
      border: 1px solid rgba(244, 63, 94, 0.25);
      color: #f43f5e;
    }

    .btn-row-delete:hover {
      background: #f43f5e;
      color: white;
    }

    .empty-state {
      text-align: center;
      padding: 60px 20px;
    }

    .empty-state i {
      font-size: 54px;
      color: #64748b;
      margin-bottom: 16px;
      display: block;
    }

    .empty-state h3 {
      font-family: 'Space Grotesk', sans-serif;
      font-size: 1.2rem;
      margin-bottom: 6px;
      color: #0f172a;
    }

    .empty-state p {
      color: #64748b;
      font-size: 0.88rem;
    }

    /* Modal Styles - Centered */
    .modal-overlay {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(15, 23, 42, 0.6);
      backdrop-filter: blur(10px);
      -webkit-backdrop-filter: blur(10px);
      z-index: 10000;
      display: flex;
      align-items: center;
      justify-content: center;
      animation: fadeIn 0.3s ease;
    }

    .modal-viewport-box {
      background: rgba(255, 255, 255, 0.98);
      backdrop-filter: blur(20px) saturate(140%);
      -webkit-backdrop-filter: blur(20px) saturate(140%);
      border: 1px solid rgba(255, 255, 255, 0.6);
      border-radius: 24px;
      padding: 40px;
      width: 90%;
      max-width: 600px;
      max-height: 90vh;
      overflow-y: auto;
      box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
    }

    .modal-viewport-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 24px;
      padding-bottom: 16px;
      border-bottom: 2px solid rgba(14, 165, 233, 0.1);
    }

    .modal-viewport-header h2 {
      font-family: 'Space Grotesk', sans-serif;
      font-size: 1.4rem;
      color: #0f172a;
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .modal-viewport-header h2 i {
      color: #0ea5e9;
    }

    .modal-close-btn {
      width: 40px;
      height: 40px;
      background: rgba(244, 63, 94, 0.1);
      border: 1px solid rgba(244, 63, 94, 0.25);
      border-radius: 12px;
      color: #f43f5e;
      cursor: pointer;
      font-size: 1.2rem;
      transition: all 0.2s;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .modal-close-btn:hover {
      background: #f43f5e;
      color: white;
      transform: rotate(90deg);
    }

    .form-group {
      margin-bottom: 20px;
    }

    .form-group label {
      display: block;
      margin-bottom: 8px;
      font-weight: 700;
      font-size: 0.85rem;
      color: #334155;
    }

    .form-group label .required {
      color: #f43f5e;
      margin-left: 4px;
    }

    .form-group input,
    .form-group textarea,
    .form-group select {
      width: 100%;
      padding: 12px 16px;
      border: 2px solid rgba(14, 165, 233, 0.15);
      border-radius: 12px;
      font-size: 14px;
      background: rgba(255, 255, 255, 0.6);
      color: #0f172a;
      outline: none;
      transition: all 0.3s ease;
      font-family: inherit;
    }

    .form-group input:focus,
    .form-group textarea:focus,
    .form-group select:focus {
      border-color: #0ea5e9;
      background: #ffffff;
      box-shadow: 0 0 0 4px rgba(14, 165, 233, 0.1);
    }

    .file-upload-wrapper {
      position: relative;
      border: 2px dashed rgba(14, 165, 233, 0.3);
      border-radius: 12px;
      padding: 16px;
      text-align: center;
      cursor: pointer;
      transition: all 0.3s ease;
      background: rgba(255, 255, 255, 0.3);
    }

    .file-upload-wrapper:hover {
      border-color: #0ea5e9;
      background: rgba(255, 255, 255, 0.5);
    }

    .file-upload-wrapper input[type="file"] {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      opacity: 0;
      cursor: pointer;
    }

    .file-upload-wrapper .upload-icon {
      font-size: 1.5rem;
      color: #0ea5e9;
      margin-bottom: 4px;
      display: block;
    }

    .file-upload-wrapper .upload-text {
      color: #64748b;
      font-size: 0.8rem;
      font-weight: 600;
    }

    .file-upload-wrapper .upload-text span {
      color: #0ea5e9;
      font-weight: 700;
    }

    .file-preview-wrapper {
      margin-top: 8px;
      padding: 8px 12px;
      background: rgba(14, 165, 233, 0.05);
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      border: 1px solid rgba(14, 165, 233, 0.1);
    }

    .file-preview-wrapper .file-info {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .file-preview-wrapper .file-info i {
      color: #0ea5e9;
      font-size: 1.2rem;
    }

    .file-preview-wrapper .file-info .file-name {
      font-weight: 600;
      font-size: 0.85rem;
      color: #0f172a;
    }

    .file-preview-wrapper .remove-file {
      background: rgba(244, 63, 94, 0.1);
      border: none;
      border-radius: 6px;
      color: #f43f5e;
      padding: 4px 10px;
      cursor: pointer;
      font-size: 0.75rem;
      font-weight: 600;
      transition: all 0.2s;
    }

    .file-preview-wrapper .remove-file:hover {
      background: #f43f5e;
      color: white;
    }

    .checkbox-wrapper {
      display: flex;
      align-items: center;
      gap: 10px;
      cursor: pointer;
      padding: 4px 0;
    }

    .checkbox-wrapper input[type="checkbox"] {
      width: 18px;
      height: 18px;
      accent-color: #0ea5e9;
      cursor: pointer;
    }

    .checkbox-wrapper label {
      font-weight: 700;
      font-size: 0.85rem;
      color: #334155;
      cursor: pointer;
      margin: 0;
    }

    .upload-progress {
      margin-top: 12px;
      background: rgba(14, 165, 233, 0.1);
      border-radius: 8px;
      height: 6px;
      overflow: hidden;
    }

    .upload-progress .progress-bar {
      height: 100%;
      background: linear-gradient(135deg, #0ea5e9, #0284c7);
      transition: width 0.3s ease;
      border-radius: 8px;
    }

    .modal-footer {
      display: flex;
      gap: 12px;
      justify-content: flex-end;
      margin-top: 24px;
      padding-top: 16px;
      border-top: 2px solid rgba(14, 165, 233, 0.1);
    }

    .btn-cancel {
      padding: 10px 24px;
      background: rgba(255, 255, 255, 0.4);
      border: 2px solid rgba(14, 165, 233, 0.2);
      border-radius: 12px;
      color: #334155;
      cursor: pointer;
      font-weight: 700;
      font-size: 0.9rem;
      transition: all 0.3s ease;
    }

    .btn-cancel:hover {
      background: #ffffff;
      border-color: #334155;
      transform: translateY(-2px);
    }

    .btn-submit {
      padding: 10px 28px;
      background: linear-gradient(135deg, #0ea5e9, #0284c7);
      color: white;
      border: none;
      border-radius: 12px;
      cursor: pointer;
      font-weight: 700;
      font-size: 0.9rem;
      transition: all 0.3s ease;
      display: inline-flex;
      align-items: center;
      gap: 10px;
    }

    .btn-submit:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 10px 25px rgba(14, 165, 233, 0.4);
    }

    .btn-submit:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    .btn-submit .spinner {
      width: 18px;
      height: 18px;
      border: 2px solid rgba(255, 255, 255, 0.3);
      border-top-color: white;
      border-radius: 50%;
      animation: spin 0.6s linear infinite;
    }

    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }

    @keyframes spin {
      to { transform: rotate(360deg); }
    }

    @media (max-width: 768px) {
      .notification-item {
        flex-direction: column;
        align-items: flex-start;
        gap: 14px;
      }
      .notification-actions-cell {
        width: 100%;
        justify-content: flex-end;
      }
      .management-toolbar {
        flex-direction: column;
        align-items: stretch;
      }
      .management-toolbar > div {
        justify-content: center;
      }
      .modal-viewport-box {
        padding: 24px;
        max-height: 95vh;
      }
      .modal-viewport-header h2 {
        font-size: 1.2rem;
      }
      .modal-footer {
        flex-direction: column;
      }
      .modal-footer button {
        width: 100%;
        justify-content: center;
      }
    }
  `;

  if (loading && notifications.length === 0) {
    return <LoadingSpinner />;
  }

  const filteredNotifications = getFilteredNotifications();

  return (
    <>
      <style>{styles}</style>
      <div className="notifications-page">
        <div className="welcome-hero">
          <h1>🔔 Notifications Management</h1>
          <p>Publish announcements, circulars, and attachments safely across institutional channels.</p>
          <div className="hero-meta-date">
            <i className="far fa-clock"></i> {currentTime}
          </div>
        </div>

        <div className="management-toolbar">
          <div>
            <select 
              className="filter-select" 
              value={filter} 
              onChange={(e) => setFilter(e.target.value)}
            >
              <option value="all">All Notifications</option>
              <option value="important">Important Only</option>
              <option value="normal">Normal Only</option>
            </select>
          </div>
          <button className="btn-action-trigger btn-blue" onClick={openCreateModal}>
            <i className="fas fa-plus-circle"></i> Add Notification
          </button>
        </div>

        <div className="notifications-container">
          {filteredNotifications.length === 0 ? (
            <div className="empty-state">
              <i className="fas fa-bell-slash"></i>
              <h3>No Notifications Found</h3>
              <p>There are no announcements currently matching your filter choice.</p>
            </div>
          ) : (
            filteredNotifications.map((item) => (
              <div className="notification-item" key={item.id}>
                <div className={`notification-icon-box ${item.important ? 'icon-important' : 'icon-normal'}`}>
                  <i className={`fas ${item.important ? 'fa-exclamation' : 'fa-bell'}`}></i>
                </div>
                <div className="notification-content-body">
                  <div className="notification-item-title">
                    {item.title}
                    {item.important && (
                      <span className="important-badge">
                        <i className="fas fa-star"></i> Important
                      </span>
                    )}
                  </div>
                  <div className="notification-item-meta">
                    <span><i className="far fa-calendar-alt"></i> {formatDate(item.createdAt)}</span>
                    <span><i className="far fa-clock"></i> {getTimeAgo(item.createdAt)}</span>
                  </div>
                  {item.fileUrl && (
                    <a href={item.fileUrl} target="_blank" rel="noopener noreferrer" className="file-attachment-pill">
                      <i className="fas fa-paperclip"></i> View Attachment
                    </a>
                  )}
                </div>
                <div className="notification-actions-cell">
                  <button className="btn-row-action btn-row-edit" onClick={() => openEditModal(item)}>
                    <i className="fas fa-pen-to-square"></i> Edit
                  </button>
                  <button className="btn-row-action btn-row-delete" onClick={() => handleDelete(item.id, item.fileUrl)}>
                    <i className="fas fa-trash-can"></i> Delete
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Notification Modal - Centered */}
        {modalOpen && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal-viewport-box" onClick={(e) => e.stopPropagation()}>
              <div className="modal-viewport-header">
                <h2>
                  <i className={`fas ${editingNotif ? 'fa-edit' : 'fa-plus-circle'}`}></i>
                  {editingNotif ? 'Edit Notification' : 'Add New Notification'}
                </h2>
                <button className="modal-close-btn" onClick={closeModal}>
                  <i className="fas fa-times"></i>
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>Notification Title <span className="required">*</span></label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Enter notification title"
                    required
                  />
                </div>

                <div className="checkbox-wrapper">
                  <input
                    type="checkbox"
                    checked={formData.important}
                    onChange={(e) => setFormData({ ...formData, important: e.target.checked })}
                    id="importantCheckbox"
                  />
                  <label htmlFor="importantCheckbox">
                    Mark as Important (High listing priority visibility)
                  </label>
                </div>

                <div className="form-group">
                  <label>Attachment (PDF, DOC, Image File)</label>
                  <div className="file-upload-wrapper">
                    <i className="fas fa-paperclip upload-icon"></i>
                    <div className="upload-text">
                      Drag & drop or <span>browse</span> to upload file
                    </div>
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                      onChange={handleFileChange}
                    />
                  </div>
                  {filePreview && (
                    <div className="file-preview-wrapper">
                      <div className="file-info">
                        <i className="fas fa-file"></i>
                        <span className="file-name">{filePreview}</span>
                      </div>
                      <button
                        type="button"
                        className="remove-file"
                        onClick={() => {
                          setFilePreview('');
                          setFile(null);
                          setFormData({ ...formData, fileUrl: '' });
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  )}
                  {formData.fileUrl && !file && (
                    <div className="file-preview-wrapper">
                      <div className="file-info">
                        <i className="fas fa-paperclip"></i>
                        <span className="file-name">Current file attached</span>
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <a 
                          href={formData.fileUrl} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="btn-row-action btn-row-edit" 
                          style={{ textDecoration: 'none', padding: '4px 12px', fontSize: '0.75rem' }}
                        >
                          View
                        </a>
                        <button 
                          type="button" 
                          className="btn-row-action btn-row-delete" 
                          onClick={() => setFormData({ ...formData, fileUrl: '' })}
                          style={{ padding: '4px 12px', fontSize: '0.75rem' }}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {isUploading && uploadProgress > 0 && (
                  <div className="upload-progress">
                    <div className="progress-bar" style={{ width: `${uploadProgress}%` }}></div>
                  </div>
                )}

                <div className="modal-footer">
                  <button type="button" className="btn-cancel" onClick={closeModal}>
                    <i className="fas fa-times"></i> Cancel
                  </button>
                  <button type="submit" className="btn-submit" disabled={isUploading}>
                    {isUploading ? (
                      <>
                        <span className="spinner"></span>
                        Uploading...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-save"></i>
                        {editingNotif ? 'Update Notification' : 'Create Notification'}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Notifications;